// services/onboardingCache.js — v2.5.146
// ─────────────────────────────────────────────────────────────────
// Module-scoped cache for the "should the user see the onboarding
// wizard right now?" check. The check is one network call per
// session (the user's onboarding state is stable across a session
// once they're logged in), so we share the result via this module
// instead of re-fetching on every PrivateRoute pass.
//
// Usage:
//   - PrivateRoute calls `fetchOnboardingState()` to check (cached
//     after first call).
//   - The wizard page calls `resetOnboardingCheck()` after a
//     successful POST so the next render sees `shouldShow=false`
//     and lets navigation proceed normally.
//   - Login/logout flows should call `resetOnboardingCheck()` to
//     avoid cross-user pollution. (AuthContext handles this; see
//     v2.5.146 changes there.)

let cache = null;          // null = unchecked, true = shouldShow, false = skip
let inFlight = null;       // dedupe in-flight requests during boot

export function getCachedOnboardingShouldShow() {
  return cache;
}

export async function fetchOnboardingState() {
  if (cache !== null) return cache;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      // Dynamic import keeps the cache module decoupled from the
      // axios client at module-load time (the api client itself
      // imports from auth context indirectly — keeping this lazy
      // avoids a circular import chain during initial bundle parse).
      const api = (await import('./api.js')).default;
      const { data } = await api.get('/me/onboarding');
      cache = !!data.shouldShow;
      return cache;
    } catch {
      // Failure path: don't block the user. Pretending the wizard
      // shouldn't show is the safer default — they'll still see the
      // normal dashboard and can find the wizard via Settings later
      // (if we add that surface).
      cache = false;
      return false;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

export function resetOnboardingCheck() {
  cache = null;
  inFlight = null;
}
